import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  signInWithGoogle,
  signOut,
  getCurrentUser,
  getSession,
  storeUserData,
} from "../../../services/supabaseAuth";

export const loginWithGoogle = createAsyncThunk(
  "auth/login-with-google",
  async (_, { rejectWithValue }) => {
    try {
      console.log("🟡 Auth: loginWithGoogle thunk started");
      const { data, error } = await signInWithGoogle();
      console.log("🟡 Auth: signInWithGoogle returned:", {
        hasData: !!data,
        error,
      });
      if (error) {
        console.error("🟡 Auth: Error from signInWithGoogle:", error);
        return rejectWithValue(error);
      }
      console.log("🟡 Auth: loginWithGoogle successful, returning data");
      return { data };
    } catch (error) {
      console.error("🟡 Auth: Exception in loginWithGoogle:", error);
      return rejectWithValue(error);
    }
  }
);

export const checkAuthSession = createAsyncThunk(
  "auth/check-session",
  async (_, { rejectWithValue }) => {
    try {
      console.log("🟡 Auth: checkAuthSession started");
      console.log("🟡 Auth: Calling getSession...");
      const { session, error } = await getSession();
      console.log("🟡 Auth: getSession result:", {
        hasSession: !!session,
        hasUser: !!session?.user,
        error,
      });
      if (error) {
        console.error("❌ Auth: getSession error:", error);
        return rejectWithValue(error);
      }
      if (session?.user) {
        console.log("🟡 Auth: Session has user, storing user data...");
        // Store user data in Supabase database
        const { data: userData, error: storeError } = await storeUserData(
          session.user
        );
        if (storeError) {
          console.error("❌ Auth: Error storing user data:", storeError);
        } else {
          console.log("🟡 Auth: User data stored successfully");
        }
        return { session, user: session.user, userData };
      }
      console.log("🟡 Auth: No session or user found");
      return { session: null, user: null, userData: null };
    } catch (error) {
      console.error("❌ Auth: checkAuthSession exception:", error);
      return rejectWithValue(error);
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await signOut();
      if (error) {
        return rejectWithValue(error);
      }
      return { success: true };
    } catch (error) {
      console.log(error);
      return rejectWithValue(error);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    session: null,
    user: null,
    userData: null,
    isLoading: false,
    error: null,
    isFetching: false,
  },

  reducers: {
    logOutUser: (state) => {
      state.session = null;
      state.user = null;
      state.userData = null;
    },
    setSession: (state, action) => {
      state.session = action.payload.session;
      state.user = action.payload.user;
    },
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(checkAuthSession.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(checkAuthSession.fulfilled, (state, action) => {
        state.isFetching = false;
        state.session = action.payload.session;
        state.user = action.payload.user;
        state.userData = action.payload.userData;
        state.error = null;
      })
      .addCase(checkAuthSession.rejected, (state, action) => {
        state.isFetching = false;
        state.session = null;
        state.user = null;
        state.userData = null;
        state.error = action.payload;
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.session = null;
        state.user = null;
        state.userData = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logOutUser, setSession, clearError } = authSlice.actions;

export default authSlice.reducer;
